'use client';

import { fetchWithBypass } from '@/lib/platform/utils/fetch';
import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  Bars3Icon,
  ChevronDownIcon,
  MoonIcon,
  SunIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useSession, signOut } from '@/lib/platform/auth/client';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { useNegotiatorMode } from '@/lib/platform/contexts/negotiator-mode-context';
import { useEntitlements } from '@/lib/platform/hooks/use-entitlements';
import { isSponsorshipsEnabled } from '@/lib/platform/config/feature-flags';
import { logger } from '@/lib/platform/utils/logger';

interface TailwindHeaderProps {
  setSidebarOpen: (open: boolean) => void;
  hideMenuButton?: boolean;
}

export default function TailwindHeader({
  setSidebarOpen,
  hideMenuButton = false,
}: TailwindHeaderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { darkMode, themePreference, setThemePreference } = useDarkMode();
  const { organization, isLoadingOrganization } = useOrganization();
  const { data: session } = useSession();
  const { isNegotiatorMode, toggleNegotiatorMode } = useNegotiatorMode();
  const { features } = useEntitlements();
  const [userName, setUserName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarError, setAvatarError] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [negotiatorDisplayMode, setNegotiatorDisplayMode] = useState<
    'emoji' | 'temp' | 'color' | 'free'
  >('emoji');

  // Reset navigating state when pathname changes (adjust-state-during-render
  // pattern, https://react.dev/learn/you-might-not-need-an-effect)
  const [prevPathname, setPrevPathname] = useState(pathname);
  if (prevPathname !== pathname) {
    setPrevPathname(pathname);
    setNavigating(false);
  }

  // Load negotiator display mode from localStorage. Deliberately a mount effect:
  // reading localStorage during render (lazy useState) would cause an SSR
  // hydration mismatch, so the one-time post-hydration setState is intentional.
  useEffect(() => {
    const savedMode = localStorage.getItem('negotiatorDisplayMode');
    if (savedMode && ['emoji', 'temp', 'color', 'free'].includes(savedMode)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNegotiatorDisplayMode(savedMode as 'emoji' | 'temp' | 'color' | 'free');
    }
  }, []);

  // Save negotiator display mode to localStorage
  const saveNegotiatorDisplayMode = (mode: 'emoji' | 'temp' | 'color' | 'free') => {
    setNegotiatorDisplayMode(mode);
    localStorage.setItem('negotiatorDisplayMode', mode);
    window.dispatchEvent(new CustomEvent('negotiator-display-mode-changed', { detail: { mode } }));
  };

  // Load user data when the session user changes (adjust-state-during-render
  // pattern, https://react.dev/learn/you-might-not-need-an-effect)
  // Initialize to undefined (NOT session?.user) so the sync block also runs on
  // the very first render when the session is already available at mount.
  const [prevUser, setPrevUser] = useState<NonNullable<typeof session>['user'] | undefined>(
    undefined
  );
  if (prevUser !== session?.user) {
    setPrevUser(session?.user);
    if (session?.user) {
      const user = session.user;
      const isAnonymous = (user as Record<string, unknown>).isAnonymous === true;

      if (isAnonymous) {
        // For anonymous users, show custom UI (optimized WebP avatar)
        setUserName('Jane Doe');
        setAvatarUrl('/demo-avatar.webp');
        setAvatarError(false);
      } else {
        // For regular users, use their actual data
        const name = user.name || '';
        const avatar = user.image || '';

        // Add cache buster if avatar exists. Uses the user's updatedAt (pure in
        // render) — it changes whenever the profile is updated, and the
        // profile-avatar-updated event below handles instant refresh on upload.
        const updatedAt = (user as Record<string, unknown>).updatedAt;
        const cacheBuster = updatedAt ? new Date(updatedAt as string | Date).getTime() : 0;
        const cacheBustedAvatar = avatar ? `${avatar}?t=${cacheBuster}` : '';

        setAvatarUrl(cacheBustedAvatar);
        setAvatarError(false); // Reset error state when loading new avatar

        // Display name logic: name, fallback to email
        setUserName(name || user.email?.split('@')[0] || 'User');
      }
    }
  }

  useEffect(() => {
    // Listen for avatar updates
    const handleAvatarUpdate = (event: CustomEvent) => {
      const { avatarUrl } = event.detail;
      logger.log('[Header] Avatar updated event received:', avatarUrl);
      // Add cache buster to force browser to reload the image
      const cacheBustedUrl = avatarUrl ? `${avatarUrl}?t=${Date.now()}` : '';
      logger.log('[Header] Setting avatar URL with cache buster:', cacheBustedUrl);
      setAvatarUrl(cacheBustedUrl);
      setAvatarError(false); // Reset error state when avatar is updated
    };

    window.addEventListener('profile-avatar-updated', handleAvatarUpdate as EventListener);

    return () => {
      window.removeEventListener('profile-avatar-updated', handleAvatarUpdate as EventListener);
    };
  }, []);

  const handleLogout = async () => {
    // Check if user is a demo user and clean up if so
    if (session?.user) {
      const isAnonymous = (session.user as Record<string, unknown>).isAnonymous === true;

      if (isAnonymous) {
        logger.log('[Header] Deleting demo user before logout');
        try {
          await fetchWithBypass('/api/demo/cleanup', {
            method: 'DELETE',
          });
        } catch (error) {
          logger.error('[Header] Error cleaning up demo user:', error);
          // Continue with logout even if cleanup fails
        }
      }
    }

    // Sign out FIRST (before clearing storage)
    try {
      await signOut();
    } catch (error) {
      logger.error('[Header] Error signing out:', error);
      // Continue with cleanup even if sign out fails
    }

    // Clear all cached data after logout
    if (typeof window !== 'undefined') {
      sessionStorage.clear();
      localStorage.clear();
    }

    // Clear React Query cache to prevent stale data (organization, logos, etc.)
    queryClient.clear();

    // Redirect to login page with full page reload to ensure fresh state
    // Use window.location.href instead of router.push to force reload
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination
    window.location.href = '/auth/login';
  };

  const isFullPageScroll = pathname?.startsWith('/plugins') || pathname?.startsWith('/welcome');

  return (
    <div
      className={`flex h-16 shrink-0 items-center gap-x-4 px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-8 ${
        isFullPageScroll ? 'sticky top-0 z-40' : ''
      } ${darkMode ? 'border-b border-white/10 bg-gray-900' : 'bg-blue-100 lg:border-b lg:border-gray-200 lg:bg-white'}`}
    >
      {!hideMenuButton && (
        <>
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className={`-m-2.5 p-2.5 lg:hidden ${darkMode ? 'text-gray-400' : 'text-gray-700'}`}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>

          {/* Separator */}
          <div
            aria-hidden="true"
            className={`h-6 w-px lg:hidden ${darkMode ? 'bg-white/10' : 'bg-gray-900/10'}`}
          />
        </>
      )}

      <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
        <div className="flex flex-1 items-center">
          <button
            onClick={() => {
              setNavigating(true);
              router.push('/dashboard');
            }}
            disabled={navigating}
            className={`no-print text-lg font-semibold transition-opacity hover:opacity-70 disabled:opacity-50 ${darkMode ? 'text-white' : 'text-gray-900'}`}
          >
            {isLoadingOrganization
              ? 'Loading...'
              : pathname === '/welcome'
                ? 'Welcome'
                : organization && organization !== undefined
                  ? organization.name
                  : 'Welcome'}
          </button>
        </div>
        <div className="flex items-center gap-x-4 lg:gap-x-6">
          {/* Separator */}
          <div
            aria-hidden="true"
            className={`hidden lg:block lg:h-6 lg:w-px ${darkMode ? 'bg-white/10' : 'bg-gray-900/10'}`}
          />

          {/* Profile dropdown */}
          <Menu as="div" className="relative">
            <MenuButton className="-m-1.5 flex items-center p-1.5">
              <span className="sr-only">Open user menu</span>
              {avatarUrl && !avatarError ? (
                // eslint-disable-next-line @next/next/no-img-element -- avatars come from OAuth providers (e.g. Microsoft) whose hosts are not enumerable in remotePatterns
                <img
                  src={avatarUrl}
                  alt="User avatar"
                  className="size-8 rounded-full object-cover"
                  onError={() => {
                    logger.warn('[Header] Avatar failed to load, falling back to default icon');
                    setAvatarError(true);
                  }}
                />
              ) : (
                <span
                  className={`inline-block size-8 overflow-hidden rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                >
                  <svg
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    className={`size-full ${darkMode ? 'text-gray-500' : 'text-gray-300'}`}
                  >
                    <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </span>
              )}
              <span className="hidden lg:flex lg:items-center">
                <span
                  className={`ml-4 text-sm/6 font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                >
                  {userName}
                </span>
                <ChevronDownIcon
                  aria-hidden="true"
                  className={`ml-2 size-5 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}
                />
              </span>
            </MenuButton>
            <MenuItems
              transition
              anchor="bottom end"
              className={`z-[60] w-56 origin-top-right rounded-lg p-1 text-sm/6 shadow-lg ring-1 transition duration-100 ease-out [--anchor-gap:0.25rem] focus:outline-none data-closed:scale-95 data-closed:opacity-0 ${
                darkMode
                  ? 'bg-gray-800 text-gray-300 ring-white/10'
                  : 'bg-white text-gray-900 ring-gray-900/5'
              }`}
            >
              <MenuItem>
                <button
                  onClick={() => {
                    setNavigating(true);
                    router.push('/profile');
                  }}
                  disabled={navigating}
                  className={`group flex w-full items-center gap-x-3 rounded-md px-3 py-2 transition disabled:opacity-50 ${
                    darkMode ? 'data-focus:bg-gray-700' : 'data-focus:bg-gray-50'
                  }`}
                >
                  <UserIcon className="size-5" />
                  {navigating ? 'Loading...' : 'Your profile'}
                </button>
              </MenuItem>

              {/* Theme Selection */}
              <MenuItem>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (themePreference === 'system') {
                      // Turn off auto - default to dark
                      setThemePreference('dark');
                    } else {
                      // Turn on auto
                      setThemePreference('system');
                    }
                  }}
                  className={`group flex w-full items-center gap-x-3 rounded-md px-3 py-2 transition ${
                    darkMode ? 'data-focus:bg-gray-700' : 'data-focus:bg-gray-50'
                  }`}
                >
                  <span
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${
                      themePreference === 'system'
                        ? 'bg-indigo-600'
                        : darkMode
                          ? 'bg-gray-600'
                          : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${
                        themePreference === 'system' ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </span>
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Theme auto
                  </span>
                </button>
              </MenuItem>

              {/* Light/Dark Buttons - Only shown when Auto is off */}
              {themePreference !== 'system' && (
                <div className="px-3 py-2">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setThemePreference('light')}
                      className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-xs transition ${
                        themePreference === 'light'
                          ? darkMode
                            ? 'border-gray-500 bg-gray-700 text-white'
                            : 'border-gray-400 bg-gray-100 text-gray-900'
                          : darkMode
                            ? 'border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <SunIcon className="size-3.5" />
                      Light
                    </button>
                    <button
                      onClick={() => setThemePreference('dark')}
                      className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-xs transition ${
                        themePreference === 'dark'
                          ? darkMode
                            ? 'border-gray-500 bg-gray-700 text-white'
                            : 'border-gray-400 bg-gray-100 text-gray-900'
                          : darkMode
                            ? 'border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <MoonIcon className="size-3.5" />
                      Dark
                    </button>
                  </div>
                </div>
              )}

              {/* Negotiator Mode Toggle - Only for Enterprise with Sponsorships */}
              {features.sponsorshipPackages &&
                isSponsorshipsEnabled() &&
                features.sponsorshipSponsors && (
                  <MenuItem>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleNegotiatorMode();
                      }}
                      className={`group flex w-full items-center gap-x-3 rounded-md px-3 py-2 transition ${
                        darkMode ? 'data-focus:bg-gray-700' : 'data-focus:bg-gray-50'
                      }`}
                    >
                      <span
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${
                          isNegotiatorMode
                            ? 'bg-indigo-600'
                            : darkMode
                              ? 'bg-gray-600'
                              : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${
                            isNegotiatorMode ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </span>
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Negotiator
                      </span>
                    </button>
                  </MenuItem>
                )}

              {/* Negotiator Display Mode - Only shown when Negotiator is on */}
              {features.sponsorshipPackages &&
                isSponsorshipsEnabled() &&
                features.sponsorshipSponsors &&
                isNegotiatorMode && (
                  <div className="px-3 py-2">
                    <div className="grid grid-cols-4 gap-1">
                      <button
                        onClick={() => saveNegotiatorDisplayMode('emoji')}
                        className={`flex items-center justify-center rounded-md border px-2 py-1.5 text-xs transition ${
                          negotiatorDisplayMode === 'emoji'
                            ? darkMode
                              ? 'border-gray-500 bg-gray-700 text-white'
                              : 'border-gray-400 bg-gray-100 text-gray-900'
                            : darkMode
                              ? 'border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        Emoji
                      </button>
                      <button
                        onClick={() => saveNegotiatorDisplayMode('temp')}
                        className={`flex items-center justify-center rounded-md border px-2 py-1.5 text-xs transition ${
                          negotiatorDisplayMode === 'temp'
                            ? darkMode
                              ? 'border-gray-500 bg-gray-700 text-white'
                              : 'border-gray-400 bg-gray-100 text-gray-900'
                            : darkMode
                              ? 'border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        Temp
                      </button>
                      <button
                        onClick={() => saveNegotiatorDisplayMode('color')}
                        className={`flex items-center justify-center rounded-md border px-2 py-1.5 text-xs transition ${
                          negotiatorDisplayMode === 'color'
                            ? darkMode
                              ? 'border-gray-500 bg-gray-700 text-white'
                              : 'border-gray-400 bg-gray-100 text-gray-900'
                            : darkMode
                              ? 'border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        Color
                      </button>
                      <button
                        onClick={() => saveNegotiatorDisplayMode('free')}
                        className={`flex items-center justify-center rounded-md border px-2 py-1.5 text-xs transition ${
                          negotiatorDisplayMode === 'free'
                            ? darkMode
                              ? 'border-gray-500 bg-gray-700 text-white'
                              : 'border-gray-400 bg-gray-100 text-gray-900'
                            : darkMode
                              ? 'border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        ABC
                      </button>
                    </div>
                  </div>
                )}

              <div className={`my-1 h-px ${darkMode ? 'bg-white/10' : 'bg-gray-100'}`} />

              <MenuItem>
                <button
                  onClick={handleLogout}
                  className={`group flex w-full items-center gap-x-3 rounded-md px-3 py-2 transition ${
                    darkMode
                      ? 'text-red-400 data-focus:bg-gray-700'
                      : 'text-red-600 data-focus:bg-gray-50'
                  }`}
                >
                  <ArrowRightOnRectangleIcon className="size-5" />
                  Sign out
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
      </div>
    </div>
  );
}
